import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

/** Command Center → a practice → Settings → Tracked Links (agency admins only). */
export interface TrackedLink {
  id: string;
  slug: string;
  name: string;
  targetUrl: string;
  /** The full short URL, ready to paste. */
  shortUrl: string;
  clickCount: number;
  lastClickAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TrackedLinksView {
  /** What every short URL hangs off, so a custom name can be previewed before it exists. */
  baseUrl: string;
  links: TrackedLink[];
}

export interface TrackedLinkClick {
  id: string;
  ip: string | null;
  userAgent: string | null;
  referer: string | null;
  clickedAt: string;
}

export interface TrackedLinkClicksView {
  link: TrackedLink;
  /** Newest first, capped at 500. `total` is the true count. */
  clicks: TrackedLinkClick[];
  total: number;
  byDay: Array<{ day: string; count: number }>;
  byIp: Array<{ ip: string; count: number; lastClickAt: string }>;
}

export interface TrackedLinkInput {
  name: string;
  targetUrl: string;
  /** Omit for a random 6-character slug. */
  slug?: string;
}

export type ResetClicksInput =
  | { mode: 'all' }
  | { mode: 'ids'; ids: string[] }
  | { mode: 'range'; from?: string; to?: string };

export const MAX_TRACKED_LINKS = 5;

const base = (practiceId: string) =>
  `/agency/practices/${encodeURIComponent(practiceId)}/tracked-links`;
const unwrap = <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => p.then((r) => r.data.data);

export const trackedLinksApi = {
  list: (practiceId: string) =>
    unwrap(apiClient.get<ApiResponse<TrackedLinksView>>(base(practiceId))),
  create: (practiceId: string, input: TrackedLinkInput) =>
    unwrap(apiClient.post<ApiResponse<TrackedLink>>(base(practiceId), input)),
  update: (practiceId: string, linkId: string, input: Partial<TrackedLinkInput>) =>
    unwrap(
      apiClient.patch<ApiResponse<TrackedLink>>(
        `${base(practiceId)}/${encodeURIComponent(linkId)}`,
        input,
      ),
    ),
  remove: (practiceId: string, linkId: string) =>
    unwrap(
      apiClient.delete<ApiResponse<{ deleted: boolean }>>(
        `${base(practiceId)}/${encodeURIComponent(linkId)}`,
      ),
    ),
  clicks: (practiceId: string, linkId: string) =>
    unwrap(
      apiClient.get<ApiResponse<TrackedLinkClicksView>>(
        `${base(practiceId)}/${encodeURIComponent(linkId)}/clicks`,
      ),
    ),
  resetClicks: (practiceId: string, linkId: string, input: ResetClicksInput) =>
    unwrap(
      apiClient.post<ApiResponse<{ deleted: number; remaining: number }>>(
        `${base(practiceId)}/${encodeURIComponent(linkId)}/clicks/reset`,
        input,
      ),
    ),
};
